<template>
    <div
        v-if="showSundaysInformation"
        :class="['shopping-sundays-info', ...getBemModifiers()]"
    >
        <StatementYellowBar
            v-if="sundayIsClose"
            class="shopping-sundays-info__statement"
            :statement-id="statementId"
            @display-changed="updateStatementDisplayState"
        >
            Nasze sklepy czynne są od poniedziałku do soboty oraz w niedziele handlowe.
        </StatementYellowBar>
        <StatementBlueBar
            v-else
            :statement-id="statementId"
            class="shopping-sundays-info__statement"
            @display-changed="updateStatementDisplayState"
        >
            Niedziela {{ nearestSundayText }} jest niedzielą handlową<br>
            <strong>i wszystkie nasze sklepy będą czynne.</strong>
        </StatementBlueBar>
    </div>
</template>

<script>
import { monthNameByIndex } from '~/src/config/dictionary/date';
import StatementYellowBar from '~/src/components/ui/Statement/StatementYellowBar';
import StatementBlueBar from '~/src/components/ui/Statement/StatementBlueBar';

export default {
    name: 'ShoppingSundaysInfo',
    availableBemModifiers: ['category', 'category-level-2', 'product-card', 'homepage'],
    components: {
        StatementYellowBar,
        StatementBlueBar,
    },
    data() {
        return {
            /**
             *  Unique id for cookie
             */
            statementId: 'ShoppingSundaysInfo',
            /**
             *  Environment variable that blocks display
             */
            disableInfo: process.env.DISABLE_SHOPPING_SUNDAYS_INFO,
            /**
             *  Start state of closest sunday
             */
            sundayIsClose: true,
            /**
             *  Start state of information availability
             */
            informationAvailable: false,
            /**
             *  Days numbers when component should display info about opened sundays
             */
            dayNumbersToDisplayOpenInfo: [0, 5, 6],
            /**
             *  Days numbers when component should display info about closed sundays
             */
            dayNumbersToDisplayClosedInfo: [0, 5, 6],
            /**
             *  Current Date
             */
            today: new Date(new Date().setHours(0, 0, 0, 0)),
            /**
             *  Start state of opened sundays dates
             */
            openSundays: null,
            /**
             * Display state from statement
             */
            statementDisplayState: true,
        };
    },
    computed: {
        /**
         * Returns whether component should be displayed
         * @returns {boolean}
         */
        showSundaysInformation() {
            return process.browser && this.informationAvailable && !this.disableInfo && this.statementDisplayState;
        },
        /**
         * Returns date of nearest sunday
         * @returns {Object}
         */
        nearestSunday() {
            return this.today.getDay() === 0
                ? this.today
                : new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() - this.today.getDay() + 7);
        },
        /**
         * Returns string of nearest sunday date
         * @returns {string}
         */
        nearestSundayText() {
            return `${this.nearestSunday.getDate()} ${monthNameByIndex[this.nearestSunday.getMonth()]}`;
        },
    },
    async mounted() {
        const { today, dayNumbersToDisplayOpenInfo, dayNumbersToDisplayClosedInfo, nearestSunday } = this;
        const dayNumber = today.getDay();
        const dayWithInfo = [...dayNumbersToDisplayOpenInfo, ...dayNumbersToDisplayClosedInfo].indexOf(dayNumber) >= 0;

        if (dayWithInfo) {
            this.openSundays = await this.getOpenSundays();

            this.openSundays.forEach((date) => {
                if (date.getTime() === nearestSunday.getTime()) {
                    this.sundayIsClose = false;
                }
            });

            this.informationAvailable = !this.sundayIsClose || (this.sundayIsClose && dayNumbersToDisplayClosedInfo.indexOf(dayNumber) >= 0);
        }
    },
    methods: {
        /**
         * Returns array with open sundays dates (string) based on current Year
         * @returns {Array}
         */
        async getOpenSundays() {
            try {
                const todayYear = this.today.getFullYear();
                // eslint-disable-next-line
                let { default: openSundaysData } = await import(`~/static/data/openSundaysByYear/${todayYear}.json` /* webpackChunkName: "data/open-sundays" */);
                let openSundaysWithYear = openSundaysData.map(data => ([todayYear, ...data]));

                const [lastMonth, lastDay] = openSundaysData[openSundaysData.length - 1];
                if (this.today.getTime() >= new Date(todayYear, lastMonth - 1, lastDay).getTime()) {
                    // eslint-disable-next-line
                    const { default: nextYearOpenSundaysData } = await import(`~/static/data/openSundaysByYear/${todayYear + 1}.json` /* webpackChunkName: "data/open-sundays-next-year" */);
                    openSundaysWithYear = [...openSundaysWithYear, ...nextYearOpenSundaysData.map(data => ([todayYear + 1, ...data]))];
                }
                return openSundaysWithYear.map(([year, month, day]) => new Date(year, month - 1, day));
            } catch {
                return [];
            }
        },
        /**
         * Set statementDisplayState
         * @param {boolean} state
         * @returns {undefined}
         */
        updateStatementDisplayState(state) {
            this.statementDisplayState = state;
        },
    },
};
</script>

<style lang="scss">
    .shopping-sundays-info {
        width: 100%;
        position: relative;

        strong {
            font-weight: $font-weight-bold;
        }

        &--category {
            margin: 18px 0 0 0;
        }

        &--category-level-2 {
            margin: 0 0 18px 0;
        }

        &--product-card {
            margin: $spacing-default 0;
        }
    }

    @media (min-width: $mf-breakpoint-tablet) {
        .shopping-sundays-info {
            &--category {
                margin: -22px 0 22px 0;
            }

            &--homepage {
                padding-top: 18px;
                background: $color-slate10;
            }

            &--product-card {
                width: calc(100% - #{2 * $spacing-default});
                margin: 0 $spacing-default 44px $spacing-default;
            }
        }
    }

    @media (min-width: $mf-breakpoint-small-desktop) {
        .shopping-sundays-info {
            &--homepage &__statement {
                margin: 0 auto;
                max-width: 946px;
            }
        }
    }

    @media (min-width: $mf-breakpoint-big-desktop) {
        .shopping-sundays-info {
            &--homepage &__statement {
                max-width: 1144px;
            }
        }
    }
</style>
